import * as React from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { lightTheme, darkTheme } from '../theme';

import * as Web from "./Web";

export const DefaultUserName = 'Anonymous';

export interface IUser {
   id?: number
   name?: string
   companyName?: string
   companyId?: number
};

export enum ThemeModes { Light = 'light', Dark = 'dark' };

export enum UserLoginStatus {
   Unknown = -1,
   LoggedOut = 0,
   InProgress = 1,
   LoggedIn = 2
}

export type UserContextType = {
   user: IUser,
   loginStatus: UserLoginStatus
   setLoginStatus: (status: UserLoginStatus) => void,
   themeMode?: ThemeModes,
   setThemeMode: (status: ThemeModes) => void,
   language: string,
   setLanguage: (status: string) => void,
};

export const UserContext = React.createContext<UserContextType>({
   user: {},
   loginStatus: UserLoginStatus.Unknown,
   setLoginStatus: (status: UserLoginStatus) => {},
   themeMode: ThemeModes.Light,
   setThemeMode: (status: ThemeModes) => {},
   language: 'en',
   setLanguage: (status: string) => {}
});

interface UserProviderProps {
   children?: any
}

export const UserProvider = ({ children }: UserProviderProps) => {
   const [user, setUser] = React.useState<IUser>({ name: DefaultUserName });
   const [loginStatus, setLoginStatus] = React.useState<UserLoginStatus>(UserLoginStatus.Unknown);
   const [themeMode, setThemeMode] = React.useState(localStorage.getItem('themeMode') || ThemeModes.Light);
   const [language, setLanguage] = React.useState(localStorage.getItem('language') || 'en');

   const theme = (themeMode === ThemeModes.Light) ? lightTheme : darkTheme;

   const userContext = {
      user,
      loginStatus,
      setLoginStatus,
      themeMode,
      setThemeMode,
      language,
      setLanguage
   } as UserContextType;

   React.useEffect(() => {
      localStorage.setItem('themeMode', themeMode);
      document.body.classList.toggle('dark-mode', themeMode === ThemeModes.Dark);
   }, [themeMode]);

   React.useEffect(() => {
      let controller = new AbortController();
      if (UserLoginStatus.LoggedIn === loginStatus) {
         fetch();
      }
      async function fetch() {
         const response = await Web.httpGet(`api/account/current`, controller);
         if (response?.errorCode) {
            if (!response.silent) {
                setUser({ id: 0, name: DefaultUserName, companyName: '', companyId: 0 });
               setLoginStatus(UserLoginStatus.LoggedOut);
               console.error(`Error logging in: ${response.errorCode}: ${response.errorText}`);
            }
         }
         else {
            setUser(response?.result);
         }
      }
      return function cleanup() {
         controller.abort();
      };
   }, [loginStatus]);

   return (
      <UserContext.Provider value={userContext}>
         <ThemeProvider theme={theme}>{children}</ThemeProvider>
      </UserContext.Provider>
   );
};

export default UserProvider;